import { last } from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { evidenceListHeaderOptions, Option } from "../../constants/index";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { generalStore } from "../../stores/GeneralStore";
import { TableStore } from "../../stores/TableStore";
import { DateRangeEnum, GetApplicantsParams, JobCategoryEnum } from "../../types/models";
import { useApplicants } from "../hooks/useApplicants";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { useTableSelection } from "../hooks/useTableSelection";
import { ConfirmationDialog } from "../ui/ConfirmationDialog";
import { HrBotApplicantsList } from "../ui/HrBotApplicantsList";
import { formatISODateOnly, getFullName } from "../util/helpers";

type Props = {
    jobCategories: Array<JobCategoryEnum>;
    tableStore: TableStore;
    lastChangeSince: Option<DateRangeEnum>;
    openDeleteDialog: boolean;
    setOpenDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>;
    setSomeSelected: React.Dispatch<React.SetStateAction<boolean>>;
};

export const RejectedList = observer(
    ({ jobCategories, tableStore, lastChangeSince, openDeleteDialog, setOpenDeleteDialog, setSomeSelected }: Props) => {
        const isMobile = useMobileMediaQuery();

        //#region loading applicants
        const options: GetApplicantsParams = {
            jobCategory: jobCategories,
            lastChangeFrom: formatISODateOnly(moment().subtract(lastChangeSince.value, "months")),
            evidence: false,
            applicationState: "denied"
        };

        const { applicants, initialLoaded, reload: reloadApplicants } = useApplicants(options, tableStore);
        //#endregion

        const selectApplicants = useTableSelection(applicants);

        // enables and disables delete and move button in parent component
        React.useEffect(() => {
            if (selectApplicants.someSelected || selectApplicants.allSelected) {
                setSomeSelected(true);
            } else {
                setSomeSelected(false);
            }
        }, [selectApplicants.allSelected, selectApplicants.someSelected, setSomeSelected]);

        //#region deletion handling
        const handleCloseDeleteDialog = () => {
            setOpenDeleteDialog(false);
        };

        const handleDeleteApplicants = async () => {
            try {
                generalStore.isLoading = true;
                await Promise.all(
                    selectApplicants.selectedIds.map(async (applicantId) => {
                        await API.deleteApplicant(applicantId);
                    })
                );

                // reload applicants
                await reloadApplicants();

                selectApplicants.clearSelection();
            } catch (err) {
                generalStore.setError(t("error.deleteApplications"), err);
            } finally {
                generalStore.isLoading = false;
                setOpenDeleteDialog(false);
            }
        };
        //#endregion

        const responsiveHeaderOptions = isMobile ? evidenceListHeaderOptions.slice(0, 2) : evidenceListHeaderOptions;

        const tableCellWidthProportionsArray = isMobile ? [1, 1] : [3, 4, 2, 2];

        return (
            <>
                <HrBotApplicantsList
                    applicants={applicants}
                    tableStore={tableStore}
                    selectElements={selectApplicants}
                    initialLoaded={initialLoaded}
                    responsiveHeaderOptions={responsiveHeaderOptions}
                    tableCellWidthProportionsArray={tableCellWidthProportionsArray}
                />
                <ConfirmationDialog
                    label={
                        selectApplicants.selectedIds.length > 1 ? (
                            <FormattedMessage
                                id="screen.rejected.deleteMultipleConfirmationText"
                                values={{
                                    applicants: `${selectApplicants
                                        .getSelectedElements()
                                        .slice(0, -1)
                                        .map(getFullName)
                                        .join(", ")}`,
                                    lastApplicant: getFullName(last(selectApplicants.getSelectedElements())),
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="screen.rejected.deleteSingleConfirmationText"
                                values={{
                                    applicant: getFullName(selectApplicants.getSelectedElements()[0]),
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        )
                    }
                    isOpen={openDeleteDialog}
                    onBack={handleCloseDeleteDialog}
                    onSubmit={handleDeleteApplicants}
                />
            </>
        );
    }
);
